import type { EasykitOverlayTask } from '@/api/easykit';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { stripHtml, formatDutchDate } from '@/utils/easykit';

interface EasykitOverlayDetailPanelProps {
  task: EasykitOverlayTask;
}

export function EasykitOverlayDetailPanel({ task }: EasykitOverlayDetailPanelProps) {
  const selectOverlay = useEasykitPlanningStore((s) => s.selectOverlay);
  const driverMap = useEasykitPlanningStore((s) => s.driverMap);

  const dateStr = task.date_deadline.slice(0, 10);
  const formattedDate = formatDutchDate(dateStr);
  const description = task.description ? stripHtml(task.description) : '';
  const installerNames = task.installerIds
    .map((id) => driverMap.get(id) ?? `Chauffeur ${id}`)
    .join(', ');
  const headline = task.projectName ?? task.soName ?? task.name;
  const subline = task.projectName || task.soName ? task.name : null;

  return (
    <div className="px-4 lg:px-8 mt-4">
      <div className="border border-slate-300 rounded-lg bg-slate-50 shadow-sm">
        {/* Header */}
        <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200">
          <div className="min-w-0 pr-2 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <span className="shrink-0 text-[10px] font-bold bg-slate-300 text-slate-700 px-1.5 py-0.5 rounded">
                PL TAAK
              </span>
              {task.stageName && (
                <span className="shrink-0 text-[10px] font-medium text-slate-600 bg-white border border-slate-300 px-1.5 py-0.5 rounded">
                  {task.stageName}
                </span>
              )}
            </div>
            <h3 className="text-base font-bold text-slate-800 truncate">
              {headline}
              {task.soName && task.projectName && (
                <span className="ml-1 font-normal text-slate-500">· {task.soName}</span>
              )}
            </h3>
            {subline && <p className="text-xs text-slate-500 truncate mt-0.5">{subline}</p>}
          </div>
          <button
            type="button"
            onClick={() => selectOverlay(null)}
            className="shrink-0 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-2 text-sm">
          <DetailRow label="Datum" value={formattedDate} />
          <DetailRow label="Projectleider" value={task.plName ?? 'Onbekend'} />
          <DetailRow label="Chauffeur" value={installerNames || '—'} />
          {task.address && <DetailRow label="Adres" value={task.address} />}
          {task.projectName && <DetailRow label="Project" value={task.projectName} />}
          {task.soName && <DetailRow label="Verkooporder" value={task.soName} />}
          {description && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1">Beschrijving</p>
              <p className="text-sm text-slate-700 whitespace-pre-line">{description}</p>
            </div>
          )}
          <p className="text-[11px] italic text-slate-400 pt-2 border-t border-slate-200">
            Alleen-lezen — deze taak hoort bij een andere projectleider.
          </p>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <span className="text-xs font-semibold text-slate-500 w-28 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-slate-800 flex-1">{value}</span>
    </div>
  );
}
